import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { Text, Pressable, StyleSheet } from 'react-native'
import { DNABox, DNAText, DNAButton, DNAChip } from '@alucio/lux-ui'
import { useOnlineStatus } from '@alucio/core/src';
import useMonitorConnection from 'src/state/context/useMonitorConnection'
import workerChannel from 'src/worker/channels/workerChannel';
import { NETWORK_STATUS_EVENT } from '@alucio/core';
import useMakeSafePromise from '@alucio/lux-ui/src/hooks/useSafePromise';
import { RecyclerListView, DataProvider, LayoutProvider, RecyclerListViewProps } from 'recyclerlistview';
import { fetchAuthSession } from 'aws-amplify/auth'
import { uploadData } from 'aws-amplify/storage'
import { useScreenNav } from 'src/components/DNA';
import { Singleton as IndexDbCrm } from 'src/classes/CRM/CRMIndexedDB';
import { usePermissions } from 'src/state/redux/selector/permissions';
import PWALoaded from 'src/state/global/PWALoaded';

const styles = StyleSheet.create({
  logRow: {
    paddingVertical: 12,
    minWidth: '51dvw',
    flex: 1,
  },
  hiddenText: {
    color: 'orange'
  },
  clientText: {
    color: 'blue'
  },
  workerText: {
    color: 'brown'
  },
  timestampText: {
    color: 'green'
  }
})

type LogItem = { origin: 'worker' | 'client', timestamp: number, log: string }
const logs: LogItem[] = []
const CONSOLE_LOG_CAPTURE_KEY = 'CAPTURE_CONSOLE_LOGS_DEBUG'

const og = {
  log: console.log,
  warn: console.warn,
  error: console.error
}

function safeStringify(obj: any, space?: number): string {
  const seen = new WeakSet();

  return JSON.stringify(obj, (key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return '[Circular Reference]';
      }
      seen.add(value);
    }
    return value;
  }, space);
}

const patchLog = () => {
  const shouldDebug = !!localStorage.getItem(CONSOLE_LOG_CAPTURE_KEY) &&
    localStorage.getItem(CONSOLE_LOG_CAPTURE_KEY) === 'true'

  if (shouldDebug) {
    console.log = (...args) => {
      const concat = args.reduce<string[]>(
        (acc, arg) => {
          if (typeof arg === 'string')
            acc.push(arg)
          else
            acc.push(safeStringify(arg))
          return acc
        },
        []
      ).join(` | `)

      logs.push({ origin: 'client', timestamp: Date.now(), log: concat })
      og.log(...args)
    }

    console.warn = (...args) => {
      const concat = args.reduce<string[]>(
        (acc, arg) => {
          if (typeof arg === 'string')
            acc.push(arg)
          else
            acc.push(safeStringify(arg))
          return acc
        },
        []
      ).join(` | `)

      logs.push({ origin: 'client', timestamp: Date.now(), log: concat })
      og.warn(...args)
    }

    console.error = (...args) => {
      const concat = args.reduce<string[]>(
        (acc, arg) => {
          if (typeof arg === 'string')
            acc.push(arg)
          else
            acc.push(safeStringify(arg))

          return acc
        },
        []
      ).join(` | `)

      logs.push({ origin: 'client', timestamp: Date.now(), log: concat })
      og.error(...args)
    }
  }
}

patchLog()

const DeviceDebug = () => {
  const reachability = useMonitorConnection(true)
  const onlineStatus = useOnlineStatus()
  const [tick, setTick] = useState<number>(0)
  const [lastObserverTime, setLastObserverTime] = useState<['ONLINE' | 'OFFLINE', string]>()
  const [, setScrollOffset] = useState<number>(0)
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'error'>('idle')
  const [ignoreList, setIgnoreList] = useState<string[]>([])
  const safePromise = useMakeSafePromise()
  const [toggleLog] = useState<'worker' | 'client' | 'both'>('both')
  const screenNav = useScreenNav()

  const rlv = useRef<RecyclerListView<any, any>>()

  const [store, setStore] = useState<Record<
    | 'estimated'
    | 'persisted'
    | 'directory'
    | 'networkInfo'
    | 'perfMeasure'
    | 'perfMem'
    | 'deviceMem'
    | 'deviceCores'
    | 'workerReady'
    | 'idbAccess'
    | 'batt'
    | 'locks'
    | 'authSession'
    | 'crmAccountSearch'
    | 'crmAccountSearchError'
    | 'workerLogs'
    | 'static'
    , any
  >>({
    estimated: undefined,
    persisted: undefined,
    directory: undefined,
    networkInfo: undefined,
    perfMeasure: undefined,
    perfMem: undefined,
    deviceCores: undefined,
    deviceMem: undefined,
    workerReady: undefined,
    idbAccess: undefined,
    batt: undefined,
    locks: undefined,
    authSession: undefined,
    crmAccountSearch: undefined,
    crmAccountSearchError: undefined,
    workerLogs: [],
    static: {
      reachability: reachability.toString(),
      lastObserverTime: `${lastObserverTime?.[0] ?? 'Unknown'} - ${lastObserverTime?.[1] ?? 'Unknown'}`,
      onlineStatus: onlineStatus?.toString() ?? 'Unknown',
      navigatorOnLine: navigator.onLine.toString(),
      agent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      cookieEnabled: navigator.cookieEnabled.toString(),
      maxTouchPoints: navigator.maxTouchPoints,
      /* eslint-disable-next-line */
      width: screen.width,
      /* eslint-disable-next-line */
      height: screen.height,
      devicePixelRatio: window.devicePixelRatio,
    },
  })

  // const logToggle = useCallback(
  //   () => {
  //     setToggleLog(p => p === 'both'
  //       ? 'worker'
  //       : p === 'worker'
  //         ? 'client'
  //         : 'both'
  //     )

  //     setScrollOffset(() => {
  //       const currentOffset = rlv.current?.getCurrentScrollOffset() ?? 0
  //       rlv.current?.scrollToOffset(0, currentOffset, true)
  //       return currentOffset
  //     })
  //   },
  //   [setToggleLog]
  // )
  const timeout = useRef<NodeJS.Timeout>()

  useEffect(
    () => {
      timeout.current = setInterval(
        () => {
          setTick(p => p + 1)
        },
        10000
      )

      const isSet = localStorage.getItem(CONSOLE_LOG_CAPTURE_KEY)

      if (!isSet) {
        localStorage.setItem(CONSOLE_LOG_CAPTURE_KEY, 'true')
        localStorage.setItem('LOG_WINDOW_LOG_LEVEL', 'debug')
        window.location.reload()
      }

      return () => clearInterval(timeout.current)
    },
    []
  )

  const permissions = usePermissions();
  const crmAccountSearchDebug = store.crmAccountSearch;
  const isCRMAccountSearchFlagEnabled = Boolean(permissions.BEAC_8117_crm_ios_local_account_search);
  const isCRMAccountSearchIndexEnabled = crmAccountSearchDebug
    ? Boolean(crmAccountSearchDebug.isAccountSearchIndexEnabled)
    : undefined;
  const crmAccountSearchIndexEnabledLabel = isCRMAccountSearchIndexEnabled === undefined
    ? 'Unknown'
    : isCRMAccountSearchIndexEnabled ? 'YES' : 'NO';
  const crmAccountSearchPath = isCRMAccountSearchIndexEnabled === undefined
    ? 'Unknown'
    : isCRMAccountSearchIndexEnabled ? 'IndexedDB' : 'Lunr';
  const crmAccountSearchBackfillMarker = crmAccountSearchDebug?.backfillMarker;
  const syncedAccountCount = crmAccountSearchBackfillMarker?.processedCount ?? 0;
  const expectedAccountCount = crmAccountSearchBackfillMarker?.accountCount ?? crmAccountSearchDebug?.accountCount ?? 0;

  useEffect(
    () => {
      const run = async () => {
        IndexDbCrm.syncIOSLocalAccountSearchFeatureFlag();
        const allResults = await safePromise.makeSafe(Promise.allSettled([
          navigator.storage.estimate(),
          navigator.storage.persisted(),
          navigator.storage.getDirectory(),
          // @ts-expect-error
          ...([performance.measureUserAgentSpecificMemory
            // @ts-expect-error
            ? performance.measureUserAgentSpecificMemory()
            : { unsupported: true }
          ]),
          new Promise((resolve, reject) => {
            // [NOTE] - navigator.serviceWorker.ready will never resolve if a service worker isn't present
            // //       holding up the rest of the values 
            const timer = setTimeout(
              () => {
                // eslint-disable-next-line
                reject('Could not get ready state')
              },
              2500
            )
            navigator?.serviceWorker?.ready.then(data => {
              clearTimeout(timer)
              resolve(data)
            })
          }),
          (async () => {
            const dbs = await indexedDB.databases()
            return Promise.all(dbs.map((db) => {
              const wrapper = new Promise(
                (resolve) => {
                  if (!db.name) {
                    resolve('Could not get name')
                  }
                  else if (db.name) {
                    const tx = indexedDB.open(db.name)
                    tx.onsuccess = () => resolve(`${db.name} - true`)
                    tx.onerror = () => resolve(`${db.name} - false`)
                  }
                }
              )

              return wrapper
            }
            ))
          })(),
          // @ts-expect-error
          navigator.getBattery?.(),
          navigator.locks.query(),
          (async () => {
            const { credentials, ...restSession } = await fetchAuthSession()
            return restSession
          })(),
          // Worker logs placeholder
          (async () => [])(),
          IndexDbCrm.getAccountSearchDebugInfo(),
        ]))

        const [
          estimated,
          persisted,
          directory,
          perfMeasure,
          workerReady,
          idbAccess,
          batt,
          locks,
          authSession,
          workerLogs,
          crmAccountSearch,
        ] = allResults
        const crmAccountSearchDebugInfo = crmAccountSearch?.status === 'fulfilled'
          ? crmAccountSearch.value
          : undefined;
        const crmAccountSearchError = crmAccountSearch?.status === 'rejected'
          ? `${crmAccountSearch.reason?.message ?? crmAccountSearch.reason}`
          : undefined;

        setStore(p => ({
          estimated,
          persisted,
          directory,
          // @ts-expect-error
          networkInfo: !!navigator.connection
            ? {
              // @ts-expect-error
              downlink: navigator.connection.downlink,
              // @ts-expect-error
              effectiveType: navigator.connection.effectiveType,
              // @ts-expect-error
              rtt: navigator.connection.rtt,
              // @ts-expect-error
              reducedUsage: navigator.connection.saveData,
            }
            : {
              notSupported: true
            },
          perfMeasure,
          // @ts-expect-error
          perfMem: performance.memory
            ? {
              // @ts-expect-error
              jsHeapSizeLimit: performance.memory.jsHeapSizeLimit,
              // @ts-expect-error
              totalJSHeapSize: performance.memory.totalJSHeapSize,
              // @ts-expect-error
              usedJSHeapSize: performance.memory.usedJSHeapSize,
            }
            : { unsupported: true },
          deviceCores: navigator.hardwareConcurrency,
          // @ts-expect-error
          deviceMem: navigator.deviceMemory ?? { unsupported: true },
          workerReady: {
            // @ts-expect-error
            scope: workerReady?.scope ?? 'unavailable',
            // @ts-expect-error
            installing: workerReady?.installing ?? 'unavailable',
            // @ts-expect-error
            waiting: workerReady?.waiting ?? 'unavailable',
            active: {
              // @ts-expect-error
              scriptURL: workerReady?.active?.scriptURL ?? 'unavailable',
              // @ts-expect-error
              state: workerReady?.active?.state ?? 'unavailable',
            }
          },
          idbAccess,
          batt: {
            // @ts-expect-error
            charging: batt?.charging ?? 'unavailable',
            // @ts-expect-error
            level: batt?.level ?? 'unavailable'
          },
          locks,
          authSession,
          crmAccountSearch: crmAccountSearchDebugInfo,
          crmAccountSearchError,
          workerLogs: [...p.workerLogs, ...(Array.isArray(workerLogs) ? workerLogs : [])],
          static: {
            reachability: reachability.toString(),
            lastObserverTime: `${lastObserverTime?.[0] ?? 'Unknown'} - ${lastObserverTime?.[1] ?? 'Unknown'}`,
            onlineStatus: onlineStatus?.toString() ?? 'Unknown',
            navigatorOnLine: navigator.onLine.toString(),
            agent: navigator.userAgent,
            platform: navigator.platform,
            language: navigator.language,
            cookieEnabled: navigator.cookieEnabled.toString(),
            maxTouchPoints: navigator.maxTouchPoints,
            /* eslint-disable-next-line */
            width: screen.width,
            /* eslint-disable-next-line */
            height: screen.height,
            devicePixelRatio: window.devicePixelRatio,
          }
        })
        )
      }

      run().catch(err => console.debug('StatError', err))
    },
    [tick]
  )

  useEffect(
    () => {
      const subscription = workerChannel
        .observable
        .filter(msg => {
          return msg.type === 'NETWORK_STATUS'
        })
        .subscribe((m) => {
          if (m.type === 'NETWORK_STATUS') {
            const msg = m as NETWORK_STATUS_EVENT
            setLastObserverTime([msg.value, new Date().toLocaleTimeString()])
          }
        });

      return () => {
        subscription?.unsubscribe?.();
      };
    },
    []
  );

  const addToIgnore = useCallback(
    (msg: string) => () => {
      const timestampRegex = (/\[(\w)+\]\s\d+:\d+\.\d+\s/)
      const [rest] = msg.split(timestampRegex).slice(-1)
      setIgnoreList(p => {
        if (p.includes(rest))
          return p
        return [...p, rest]
      })
    },
    [setIgnoreList]
  )

  const removeFromIgnore = useCallback(
    (msg: string) => () => {
      setIgnoreList(p => p.filter(ig => ig !== msg))
    },
    [setIgnoreList]
  )

  const dataProviderInit = useMemo(
    () => (new DataProvider((prev: string, next: string) => prev !== next))
      .cloneWithRows(
        [
          ...(
            ['worker', 'both'].includes(toggleLog)
              ? store.workerLogs
              : []
          ),
          ...(
            ['client', 'both'].includes(toggleLog)
              ? logs.filter(log => !ignoreList.some(ignore => log.log.includes(ignore)))
              : []
          ),
        ].sort((a: LogItem, b: LogItem) => a.timestamp - b.timestamp)
      ),
    [tick, store.workerLogs, ignoreList, toggleLog]
  );

  const layoutProvider = useMemo(
    () => new LayoutProvider(_ => 0,
      (_, dim) => {
        dim.width = 2000;
        dim.height = 70;
      },
    ),
    []
  );

  const ignoreLong = useMemo(
    () => [
      // eslint-disable-next-line
      `{"closed":false,"_parentage":null,"_finalizers":null,"isStopped":false,"destination":{"partialObserver":{}}},{"closed":false,"_parentage":null,"_finalizers":null,"isStopped":false,"destination":{"partialObserver":{}}}`
    ],
    []
  )

  const rowRenderer = useCallback<RecyclerListViewProps['rowRenderer']>(
    (type, data: LogItem) => {
      const toRender = ignoreLong.some(ignore => data.log.includes(ignore))
        ? data.log.slice(0, 1000)
        : data.log

      return (
        <Pressable onLongPress={addToIgnore(data.log)} style={styles.logRow}>
          <Text>
            <Text style={data.origin === 'client' ? styles.clientText : styles.workerText}>[{data.origin}] - </Text>
            <Text style={styles.timestampText}>{new Date(data.timestamp).toLocaleTimeString()} - </Text>
            {toRender}
          </Text>
          {
            data.log !== toRender
              ? <Text style={styles.hiddenText}>(...long message hidden) </Text>
              : null
          }
        </Pressable>
      );
    },
    []
  )

  const scrollUp = useCallback(
    () => {
      setScrollOffset(p => {
        if (p === 0) {
          return p
        }
        if (p <= 1000) {
          rlv.current?.scrollToTop(true)
          return 0
        }
        else {
          rlv.current?.scrollToOffset(0, p - 1000, true)
          return p - 1000
        }
      })
    },
    []
  )

  const scrollDown = useCallback(
    () => {
      setScrollOffset(p => {
        if ((p + 1000 >= rlv.current!.getContentDimension().height)) {
          rlv.current?.scrollToEnd(true)
          return rlv.current!.getContentDimension().height
        }
        else {
          rlv.current?.scrollToOffset(0, p + 1000, true)
          return p + 1000
        }
      })
    },
    []
  )

  const dumpLogs = useCallback(
    () => {
      const { workerLogs, ...restStore } = store
      const combinedLogs = [...workerLogs, ...logs]
        .sort((a: LogItem, b: LogItem) => a.timestamp - b.timestamp)
        .map(
          (logItem: LogItem) => (
            `[${logItem.origin}] - ${new Date(logItem.timestamp).toISOString()} - ${logItem.log}`
          )
        )

      const textContent = JSON.stringify(restStore, null, `\t`) +
        '\n' +
        combinedLogs.join('\n')
      const name = `device-logs-${new Date().toISOString()}.txt`

      setUploadStatus('uploading')

      uploadData({
        path: ({ identityId }) => `private/${identityId}/${name}`,
        data: new Blob([textContent], { type: 'text/plain' }),
        options: {
          contentType: 'text/plain',
          contentDisposition: `attachment; filename="${name}"`,
        },
      }).result.then(() => setUploadStatus('idle')).catch(() => setUploadStatus('error'))
    },
    [store]
  )

  const disableAndReload = useCallback(
    () => {
      localStorage.removeItem('LOG_WINDOW_LOG_LEVEL')
      localStorage.removeItem(CONSOLE_LOG_CAPTURE_KEY)
      screenNav.goTo.HOME()
      window.location.reload()
    },
    [screenNav.goTo],
  )

  return (
    <DNABox appearance="col" spacing="lg">
      <DNABox appearance="col">
        <DNAText h2>CRM Account Search</DNAText>
        <DNAText>
          Feature flag BEAC_8117_crm_ios_local_account_search: {isCRMAccountSearchFlagEnabled ? 'ON' : 'OFF'}
        </DNAText>
        <DNAText>PWA loaded: {PWALoaded.isPWA ? 'YES' : 'NO'}</DNAText>
        <DNAText>
          IndexedDB account search enabled: {crmAccountSearchIndexEnabledLabel}
        </DNAText>
        <DNAText>Active CRM account search path: {crmAccountSearchPath}</DNAText>
        {
          store.crmAccountSearchError
            ? <DNAText>Debug read error: {store.crmAccountSearchError}</DNAText>
            : null
        }
        <DNAText>
          Synced accounts: {syncedAccountCount} / {expectedAccountCount}
        </DNAText>
        <DNAText>
          Current local account rows: {crmAccountSearchDebug?.accountCount ?? 'Unknown'}
        </DNAText>
        <DNAText>
          Indexed account-search tokens: {crmAccountSearchDebug?.indexedTokenCount ?? 'Unknown'}
        </DNAText>
        <DNAText>
          Backfill marker: {JSON.stringify(crmAccountSearchBackfillMarker ?? null)}
        </DNAText>
      </DNABox>

      <DNABox appearance='col'>
        <DNAText h2>Connection</DNAText>
        <DNAText>Reachability (Navigator + SWorker): {store.static.reachability}</DNAText>
        <DNAText>
          Last connection message from worker: {store.static.lastObserverTime}
        </DNAText>
        <DNAText>Online Status (useOnlineStatus): {store.static.onlineStatus}</DNAText>
        <DNAText>navigator.onLine : {store.static.navigatorOnLine}</DNAText>
        <DNAText>Network Information (not widely supported): {JSON.stringify(store.networkInfo)}</DNAText>
      </DNABox>

      <DNABox appearance='col'>
        <DNAText h2>Storage</DNAText>
        <DNAText>Estimated: {JSON.stringify(store.estimated)}</DNAText>
        <DNAText>Used: {
          store.estimated?.usage && store.estimated?.quota
            ? ((store.estimated.usage / store.estimated.quota) * 100).toFixed(2) + '%'
            : 'Unknown'
        }
        </DNAText>
        <DNAText>Persisted: {JSON.stringify(store.persisted)}</DNAText>
        <DNAText>Directory: {JSON.stringify(store.directory)}</DNAText>
      </DNABox>

      <DNABox appearance='col'>
        <DNAText h2>Memory (Not widely supported)</DNAText>
        <DNAText>performance.measureUserAgentSpecificMemory - {JSON.stringify(store.perfMeasure)}</DNAText>
        <DNAText>performance.memory - {JSON.stringify(store.perfMem)}</DNAText>
        <DNAText>navigator.deviceMemory - {JSON.stringify(store.deviceMem)} (GB)</DNAText>
        <DNAText>navigator.hardwareConcurrency - {JSON.stringify(store.deviceCores)} (threads)</DNAText>
        <DNAText>navigator.battery (deprecated) - {JSON.stringify(store.batt)}</DNAText>
      </DNABox>

      <DNABox appearance='col'>
        <DNAText h2>Browser</DNAText>
        <DNAText>userAgent - {store.static.agent}</DNAText>
        <DNAText>platform - {store.static.platform}</DNAText>
        <DNAText>language - {store.static.language}</DNAText>
        <DNAText>cookieEnabled - {store.static.cookieEnabled}</DNAText>
        <DNAText>maxTouchPoints - {store.static.maxTouchPoints}</DNAText>
        <DNAText>screen width & screen height - {store.static.width} - {store.static.height}</DNAText>
        <DNAText>devicePixelRatio - {store.static.devicePixelRatio}</DNAText>
        <DNAText>navigator.locks - {JSON.stringify(store.locks)}</DNAText>

      </DNABox>

      <DNABox appearance='col'>
        <DNAText h2>Service Worker</DNAText>
        <DNAText>Ready: {JSON.stringify(store.workerReady)}</DNAText>
      </DNABox>

      <DNABox appearance='col'>
        <DNAText h2>IndexedDB</DNAText>
        <DNAText>Databases (can open): {JSON.stringify(store.idbAccess)}</DNAText>
      </DNABox>

      <DNABox appearance='col'>
        <DNAText h2>Auth</DNAText>
        <DNAText>Auth Session: {JSON.stringify(store.authSession, null, '\t')}</DNAText>
      </DNABox>

      <DNABox
        appearance='col'
        spacing='sm'
        fill
        childFill={1}
      >
        <DNAText h2>Logger</DNAText>
        <DNABox appearance='row' spacing='lg' fill alignY='center' wrap='start'>
          <DNAButton onPress={scrollUp}>Scroll Up</DNAButton>
          <DNAButton onPress={scrollDown}>Scroll Down</DNAButton>
          {/* <DNAButton onPress={logToggle}>Toggle Logs ({toggleLog})</DNAButton> */}
          <DNAButton
            onPress={dumpLogs}
            status={
              uploadStatus === 'idle'
                ? 'primary'
                : uploadStatus === 'uploading'
                  ? 'warning'
                  : 'danger'
            }
          >
            {
              uploadStatus === 'idle'
                ? 'Dump Logs'
                : uploadStatus === 'uploading'
                  ? 'Uploading'
                  : 'Error'
            }
          </DNAButton>
          <DNAButton onPress={disableAndReload}>
            Disable and reload
          </DNAButton>
          {ignoreList.map((ignore) => (
            <DNAChip key={ignore} onPress={removeFromIgnore(ignore)} iconLeft='x'>{ignore}</DNAChip>
          ))}
        </DNABox>
        <RecyclerListView
          ref={rlv as any}
          style={{ minWidth: 600, width: '100%', minHeight: 500, maxHeight: 500 }}
          layoutProvider={layoutProvider}
          dataProvider={dataProviderInit}
          rowRenderer={rowRenderer}
          canChangeSize
          forceNonDeterministicRendering
        />
      </DNABox>

    </DNABox>
  )
}

export default DeviceDebug
